/** @module components/image-loader */
import baustein from "baustein";
import domFactory from "web/script/dom/factory";
import events from "web/script/dom/events";
import templateLoadingSpinner from "templates/loading-spinner.html";

const LOADING_CSS_CLASS = "loading";

/**
 * The image loader will preload an image with a loading spinner and then display it.
 * @constructor
 * @alias module:components/image-loader
 * @extends module:components/components
 */
export default baustein.register(
    "image-loader",
    /** @lends module:components/image-loader.prototype */
    {
        setupEvents: function(add) {
            add("click", this._onImageClick);
        },

        init: function() {
            var spinner = domFactory.fromString(templateLoadingSpinner());
            this.el.appendChild(spinner);

            var mainImage = this.el.querySelector("img");
            events.doOnce(mainImage, "load", this._onInitialMainImageLoaded.bind(this));

            // Only initialize when we have more images then space to display and we have transform support
            this.refresh();

            // detect if the image has already loaded
            // there is an Image.complete, but it's not guaranteed to return anything for an image
            // not created in JS, so try and get the naturalWidth which should return something
            // non-zero if it's loaded. IE8 doesn't support this though, so just assume it's loaded
            var loaded = false;
            if ("naturalWidth" in mainImage) {
                loaded = mainImage.naturalWidth > 0;
            } else {
                loaded = true;
            }

            if (loaded) {
                this._onInitialMainImageLoaded();
            }
        },

        /**
         * Loads an image.
         * @param {String} url - url of the image to load
         * @param {String} url2x - url of the 2x image to load
         */
        loadImage: function(url, url2x) {
            // trigger loading on the main images
            this.el.querySelector("img").classList.add(LOADING_CSS_CLASS);
            this.el.querySelector(".loading-square").classList.remove("hidden");

            // creates a fake img to preload it and oberves the load event to add it to the DOM
            var preloader = document.createElement("img");
            events.doOnce(preloader, "load", this._onMainImageLoaded.bind(this));
            preloader.src = url;
            if (url2x) {
                preloader.srcset = `${url} 1x, ${url2x} 2x`;
            }
        },

        /**
         * Updates the main image container height based on the height of the image. Sometimes,
         * this may be called too soon, so just set it to auto if getting the height returns 0.
         * We lose the animation, but at least the image isn't lost.
         */
        refresh: function() {
            var height = this.el.querySelector("img").style.clientHeight;
            if (height) {
                height += "px";
            } else {
                height = "auto";
            }
            this.el.style.height = height;
        },

        /**
         * Updates the main image with the url of the target, removes loading classes and scales the main image
         * container height
         *
         * @param event
         */
        _onMainImageLoaded: function(event) {
            var image = this.el.querySelector("img");
            image.src = event.target.src;
            image.srcset = event.target.srcset;
            image.classList.remove(LOADING_CSS_CLASS);
            this.refresh();
            this.el.querySelector(".loading-square").classList.add("hidden");
            this.emit("mainImageLoaded");
        },

        /**
         * When the initial image has loaded, because there is no need to re-load the image
         * (it isn't in a preloader), then just remove the loading square and resize the image
         * container.
         * @private
         */
        _onInitialMainImageLoaded: function() {
            // it's possible that the image took a while to load and the component has since been destroyed.
            if (this.isDestroyed()) {
                return;
            }

            this.refresh();
            this.el.querySelector(".loading-square").classList.add("hidden");
        },

        _onImageClick: function(event) {
            event.preventDefault();
            this.emit("imageClick");
        },
    }
);
